if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
          featuredGridFeatured: '[data-featured-grid-featured]',
          featuredGridRail: '[data-featured-grid-rail]',
          featuredGridIndicator: '[data-featured-grid-indicator]',
          featuredGridIndicatorThumb: '[data-featured-grid-indicator-thumb]',
          pairedCarouselIndicator: '[data-paired-carousel-indicator]',
          pairedCarouselIndicatorThumb: '[data-paired-carousel-indicator-thumb]',
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.activeFeaturedMedia = null;
        this.featuredGridEventsBound = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      disconnectedCallback() {
        this.unbindFeaturedGridEvents();
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.isPairedCarousel = this.dataset.pairedCarousel === 'true';
        this.context = this.dataset.context;
        this.variantMediaFilterEnabled = this.dataset.variantMediaFilter === 'true';
        // Master thumbnail node list, captured before variant media filtering
        // can detach nodes from the DOM. References stay stable for the
        // element's lifetime (this.elements.mediaItems serves the same purpose
        // for viewer slides).
        this._allThumbItems = [...this.querySelectorAll('.product__thumbs-item')];
        // null = show everything; otherwise a Set of visible media id strings.
        this.visibleMediaIds = null;
        this.handleFeaturedGridScroll = FoxTheme.utils.throttle(this.updateFeaturedGridIndicator.bind(this));
        this.handleFeaturedGridResize = FoxTheme.utils.throttle(this.updateFeaturedGridIndicator.bind(this));
        this.handleFeaturedGridKeydown = this.onFeaturedGridKeydown.bind(this);
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
          case 'paired-carousel':
            this.sliderOptions.breakpoints = {
              ...this.sliderOptions.breakpoints,
              768: {
                ...(this.sliderOptions.breakpoints?.[768] || {}),
                slidesPerView: 2,
                slidesPerGroup: 2,
                spaceBetween: 0,
                autoHeight: false,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.deactivateFeaturedGrid();
          this.initSlider();
          this.syncActiveFeaturedMediaWithSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.deactivateFeaturedGrid();
            this.initSlider();
          } else {
            this.destroySlider();
            if (this.isFeaturedGridLayout()) {
              this.activateFeaturedGrid();
            }
          }
        }
      }

      isFeaturedGridLayout() {
        return this.mediaLayout === 'featured-grid' && this.elements.featuredGridFeatured && this.elements.featuredGridRail;
      }

      getOriginalMediaItems() {
        // This is the original static set of media nodes. Items are moved between
        // containers in featured-grid mode and detached/reattached by the variant
        // media filter, but the node references remain stable.
        const allItems = [...this.elements.mediaItems];
        const visibleItems = this.visibleMediaIds
          ? allItems.filter((item) => this.visibleMediaIds.has(String(item.dataset.mediaId)))
          : allItems;

        return visibleItems.sort((a, b) => {
          const positionA = Number(a.dataset.mediaPosition || 0);
          const positionB = Number(b.dataset.mediaPosition || 0);
          return positionA - positionB;
        });
      }

      getSortedMediaItems(featuredMedia = this.activeFeaturedMedia) {
        const mediaItems = this.getOriginalMediaItems();
        const featuredMediaId = featuredMedia?.id ? String(featuredMedia.id) : null;

        if (featuredMediaId) {
          mediaItems.some((media, index) => {
            if (media.dataset.mediaId === featuredMediaId) {
              const [element] = mediaItems.splice(index, 1);
              mediaItems.unshift(element);
              return true;
            }
          });
        }

        this.updateMediaIndexes(mediaItems);
        return mediaItems;
      }

      updateMediaIndexes(mediaItems) {
        mediaItems.forEach((media, index) => {
          media.dataset.mediaIndex = index;

          const zoomButton = media.querySelector('.js-photoswipe--zoom');
          if (zoomButton) {
            zoomButton.dataset.mediaIndex = index;
          }
        });

        this.refreshLightboxDataSource(mediaItems);
      }

      buildLightboxDataSource(mediaItems) {
        return mediaItems.reduce((dataSource, media) => {
          const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

          let source = {
            id: mediaIndex,
            mediaType: mediaType,
          };

          switch (mediaType) {
            case 'model':
            case 'video':
            case 'external_video': {
              const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

              source = {
                ...source,
                html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`,
              };
              break;
            }
            default:
              source = {
                ...source,
                src: src,
                width: pswpWidth,
                height: pswpHeight,
              };
              break;
          }

          dataSource.push(source);
          return dataSource;
        }, []);
      }

      refreshLightboxDataSource(mediaItems) {
        if (!this.lightbox || !Array.isArray(mediaItems)) return;
        this.lightbox.options.dataSource = this.buildLightboxDataSource(mediaItems);
      }

      activateFeaturedGrid(featuredMedia = this.activeFeaturedMedia) {
        if (!this.isFeaturedGridLayout()) return;

        const mediaItems = this.getSortedMediaItems(featuredMedia);
        const featuredItems = mediaItems.slice(0, 2);
        const railItems = mediaItems.slice(2);

        this.classList.add('media-gallery--featured-grid-active');
        this.elements.featuredGridFeatured.innerHTML = '';
        this.elements.featuredGridRail.innerHTML = '';

        featuredItems.forEach((media) => {
          this.elements.featuredGridFeatured.appendChild(media);
        });

        railItems.forEach((media) => {
          this.elements.featuredGridRail.appendChild(media);
        });

        this.elements.featuredGridRail.scrollLeft = 0;
        this.bindFeaturedGridEvents();
        requestAnimationFrame(() => this.updateFeaturedGridIndicator());
      }

      deactivateFeaturedGrid() {
        if (!this.isFeaturedGridLayout()) return;

        const mediaItems = this.getOriginalMediaItems();

        this.classList.remove('media-gallery--featured-grid-active');
        this.unbindFeaturedGridEvents();
        this.elements.mediaList.innerHTML = '';

        mediaItems.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        this.updateMediaIndexes(mediaItems);
        this.updateFeaturedGridIndicator();
      }

      bindFeaturedGridEvents() {
        if (!this.isFeaturedGridLayout() || this.featuredGridEventsBound) return;

        this.elements.featuredGridRail.addEventListener('scroll', this.handleFeaturedGridScroll, { passive: true });
        this.elements.featuredGridRail.addEventListener('keydown', this.handleFeaturedGridKeydown);
        window.addEventListener('resize', this.handleFeaturedGridResize);
        this.featuredGridEventsBound = true;
      }

      unbindFeaturedGridEvents() {
        if (!this.isFeaturedGridLayout() || !this.featuredGridEventsBound) return;

        this.elements.featuredGridRail.removeEventListener('scroll', this.handleFeaturedGridScroll);
        this.elements.featuredGridRail.removeEventListener('keydown', this.handleFeaturedGridKeydown);
        window.removeEventListener('resize', this.handleFeaturedGridResize);
        this.featuredGridEventsBound = false;
      }

      onFeaturedGridKeydown(event) {
        const rail = this.elements.featuredGridRail;

        if (!rail) return;

        const scrollStep = Math.max(rail.clientWidth / 2, 1);

        switch (event.key) {
          case 'ArrowRight':
            event.preventDefault();
            rail.scrollBy({ left: scrollStep, behavior: 'smooth' });
            break;
          case 'ArrowLeft':
            event.preventDefault();
            rail.scrollBy({ left: scrollStep * -1, behavior: 'smooth' });
            break;
          case 'Home':
            event.preventDefault();
            rail.scrollTo({ left: 0, behavior: 'smooth' });
            break;
          case 'End':
            event.preventDefault();
            rail.scrollTo({ left: rail.scrollWidth, behavior: 'smooth' });
            break;
        }
      }

      updateFeaturedGridIndicator() {
        if (!this.isFeaturedGridLayout()) return;

        const rail = this.elements.featuredGridRail;
        const indicator = this.elements.featuredGridIndicator;
        const thumb = this.elements.featuredGridIndicatorThumb;

        if (!rail || !indicator || !thumb || !this.classList.contains('media-gallery--featured-grid-active')) {
          indicator?.classList.remove('is-visible');
          return;
        }

        const overflow = rail.scrollWidth - rail.clientWidth;

        if (overflow <= 1) {
          indicator.classList.remove('is-visible');
          thumb.style.width = '';
          thumb.style.transform = '';
          return;
        }

        indicator.classList.add('is-visible');
        const trackWidth = indicator.clientWidth;
        const thumbWidth = Math.max((rail.clientWidth / rail.scrollWidth) * trackWidth, 40);
        const maxTranslate = Math.max(trackWidth - thumbWidth, 0);
        const progress = overflow > 0 ? rail.scrollLeft / overflow : 0;

        thumb.style.width = `${thumbWidth}px`;
        thumb.style.transform = `translateX(${maxTranslate * progress}px)`;
      }

      syncActiveFeaturedMediaWithSlider() {
        const slider = this.sliderInstance?.slider;

        if (!slider || !this.activeFeaturedMedia) return;

        const slideIndex = this.getFeaturedMediaSlideIndex(this.activeFeaturedMedia);
        if (slideIndex === null) return;

        slider.slideTo(slideIndex, 0, false);
      }

      getFeaturedMediaSlideIndex(featuredMedia) {
        if (!featuredMedia) return null;

        const featuredMediaId = featuredMedia.id ? String(featuredMedia.id) : null;

        // When the variant media filter is active, global media positions no
        // longer match slide indexes, so look up by media id within the
        // visible items instead.
        if (this.visibleMediaIds) {
          if (!featuredMediaId) return null;

          const filteredIndex = this.getOriginalMediaItems().findIndex(
            (media) => media.dataset.mediaId === featuredMediaId
          );
          return filteredIndex >= 0 ? filteredIndex : null;
        }

        const explicitPosition = Number(featuredMedia.position);
        if (Number.isFinite(explicitPosition) && explicitPosition > 0) {
          return explicitPosition - 1;
        }

        if (!featuredMediaId) return null;

        const fallbackIndex = this.getOriginalMediaItems().findIndex((media) => media.dataset.mediaId === featuredMediaId);
        return fallbackIndex >= 0 ? fallbackIndex : null;
      }

      getMediaVariantIds(mediaItem) {
        return (mediaItem.dataset.variantIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
      }

      // Variant media filtering (v1): hides media attached to other variants.
      // Media attached to no variant always stays visible, and a variant with
      // no attached media disables filtering (full gallery shown).
      // Future extensibility: also match media alt text against option values
      // so multiple images can be grouped under one variant.
      applyVariantMediaFilter(variant) {
        if (!this.variantMediaFilterEnabled) return;

        const variantId = variant?.id ? String(variant.id) : null;
        let visibleMediaIds = null;

        if (variantId) {
          const variantHasMedia = this.elements.mediaItems.some((item) =>
            this.getMediaVariantIds(item).includes(variantId)
          );

          if (variantHasMedia) {
            visibleMediaIds = new Set(
              this.elements.mediaItems
                .filter((item) => {
                  const ids = this.getMediaVariantIds(item);
                  return ids.length === 0 || ids.includes(variantId);
                })
                .map((item) => String(item.dataset.mediaId))
            );
          }
        }

        const previous = this.visibleMediaIds;
        const changed =
          previous === null || visibleMediaIds === null
            ? previous !== visibleMediaIds
            : previous.size !== visibleMediaIds.size ||
              [...visibleMediaIds].some((id) => !previous.has(id));

        this.visibleMediaIds = visibleMediaIds;

        if (changed) this.rebuildVisibleMediaItems();
      }

      rebuildVisibleMediaItems() {
        const mediaItems = this.getOriginalMediaItems();

        if (this.elements.thumbnails && this._allThumbItems.length > 0) {
          const thumbsWrapper = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');

          if (thumbsWrapper) {
            const visibleThumbs = this._allThumbItems.filter(
              (thumb) => !this.visibleMediaIds || this.visibleMediaIds.has(String(thumb.dataset.target))
            );
            thumbsWrapper.innerHTML = '';
            visibleThumbs.forEach((thumb) => thumbsWrapper.appendChild(thumb));
            this.thumbsInstance?.slider?.update();
          }
        }

        if (this.isFeaturedGridLayout() && !FoxTheme.config.mqlMobile) {
          this.activateFeaturedGrid(this.activeFeaturedMedia);
        } else {
          this.elements.mediaList.innerHTML = '';
          mediaItems.forEach((media) => this.elements.mediaList.appendChild(media));
          this.updateMediaIndexes(mediaItems);
        }

        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.update();
        }

        if (this.isPairedCarousel && this.sliderInstance?.slider) {
          this.handlePairedCarouselThumbs();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();

          if (this.isPairedCarousel) {
            this.handlePairedCarouselThumbs();
          }
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        const dataSource = this.buildLightboxDataSource(allMedia);

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox?.pswp?.currIndex) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          const slider = this.sliderInstance?.slider;

          if (slider && lastLightboxIndex !== null) {
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handlePairedCarouselThumbs() {
        if (!this.isPairedCarousel || !this.elements.thumbnails) return;

        const thumbs = [...this.elements.thumbnails.querySelectorAll('.product__thumbs-item')];
        const indicator = this.elements.pairedCarouselIndicator;
        const indicatorThumb = this.elements.pairedCarouselIndicatorThumb;

        const setSelected = (index) => {
          thumbs.forEach((t) => t.classList.remove('paired-thumb-selected'));
          if (thumbs[index]) thumbs[index].classList.add('paired-thumb-selected');
        };

        const updateIndicator = (activeIndex) => {
          if (!indicator || !indicatorThumb) return;
          const totalSlides = thumbs.length;
          const totalGroups = Math.ceil(totalSlides / 2);
          if (totalGroups <= 1) {
            indicator.classList.remove('is-visible');
            return;
          }
          const currentGroup = Math.floor(activeIndex / 2);
          indicator.classList.add('is-visible');
          const trackWidth = indicator.clientWidth;
          const thumbWidth = Math.max((1 / totalGroups) * trackWidth, 40);
          const maxTranslate = Math.max(trackWidth - thumbWidth, 0);
          const progress = totalGroups > 1 ? currentGroup / (totalGroups - 1) : 0;
          indicatorThumb.style.width = `${thumbWidth}px`;
          indicatorThumb.style.transform = `translateX(${maxTranslate * progress}px)`;
        };

        // set initial state
        setSelected(0);
        this.sliderInstance.slider.on('afterInit', (swiper) => updateIndicator(swiper.activeIndex));
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          updateIndicator(swiper.activeIndex);
          setSelected(swiper.activeIndex);
        });
        // trigger immediately since slider is already initialized at this point
        requestAnimationFrame(() => updateIndicator(this.sliderInstance.slider.activeIndex || 0));

        if (this._pairedCarouselAbortController) {
          this._pairedCarouselAbortController.abort();
        }
        this._pairedCarouselAbortController = new AbortController();

        this.elements.thumbnails.addEventListener('click', (e) => {
          const thumbItem = e.target.closest('.product__thumbs-item');
          if (!thumbItem) return;
          const currentThumbs = [...this.elements.thumbnails.querySelectorAll('.product__thumbs-item')];
          const clickedIndex = currentThumbs.indexOf(thumbItem);
          if (clickedIndex < 0) return;
          const pairStart = Math.floor(clickedIndex / 2) * 2;
          const slider = this.sliderInstance?.slider;
          if (slider) {
            slider.slideTo(pairStart);
          }
          setSelected(clickedIndex);
        }, { signal: this._pairedCarouselAbortController.signal });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        this.applyVariantMediaFilter(variant);

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        this.activeFeaturedMedia = featuredMedia;

        if (this.sliderInstance.slider) {
          const slideIndex = this.getFeaturedMediaSlideIndex(featuredMedia);
          if (slideIndex === null) return;

          this.sliderInstance.slider.slideTo(slideIndex, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        this.activeFeaturedMedia = featuredMedia;

        if (this.isFeaturedGridLayout() && !FoxTheme.config.mqlMobile) {
          this.activateFeaturedGrid(featuredMedia);
        } else {
          const newMedias = this.getSortedMediaItems(featuredMedia);

          this.elements.mediaList.innerHTML = '';
          newMedias.forEach((media) => {
            this.elements.mediaList.appendChild(media);
          });
        }

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
